import { useState, useEffect, useRef } from "react";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import Logo from "/Logo.png";
import "./HomePage.css";

/* ─── CONVERSATION DATA ─────────────────────────────────────────── */
const heroConversations = [
  {
    agent: "EVA",
    color: "#C8813A",
    lightColor: "#F5E6D3",
    messages: [
      { from: "guest", text: "We just landed. Is early check-in possible?" },
      { from: "ai",    text: "Room 412 is ready — I'll send the key link now 🗝️" },
    ],
  },
  {
    agent: "DINE",
    color: "#3A7A57",
    lightColor: "#D4EDE0",
    messages: [
      { from: "guest", text: "Table for 4 tonight, somewhere quiet?" },
      { from: "ai",    text: "Reserved — terrace corner at 8 PM 🌿 Shall I add the tasting menu?" },
    ],
  },
  {
    agent: "SERENE",
    color: "#6B4E92",
    lightColor: "#E8DFF2",
    messages: [
      { from: "guest", text: "Any availability for a couple's massage tomorrow?" },
      { from: "ai",    text: "11 AM is open ✨ Shall I book both slots together?" },
    ],
  },
];

const evaChat = [
  { from: "guest", text: "We just arrived. Can I upgrade to a sea-view room?" },
  { from: "ai",    text: "Welcome! 🌊 A Deluxe Sea View is available for +$45/night. Shall I apply the upgrade?" },
  { from: "guest", text: "Yes please!" },
  { from: "ai",    text: "Done ✅ Room upgraded. Your new key has been sent." },
];

const dineChat = [
  { from: "guest", text: "Table for 2 tonight at 8pm?" },
  { from: "ai",    text: "Confirmed 🍽️ Table for 2 at 8 PM. Tonight's chef's menu features seared scallops to lava cake." },
  { from: "guest", text: "Perfect — add the wine pairing?" },
  { from: "ai",    text: "Done! Sommelier selection added. You're all set." },
];

const sereneChat = [
  { from: "guest", text: "Can I book a deep tissue massage Saturday?" },
  { from: "ai",    text: "🌿 Saturday slots: 11:00 AM · 2:00 PM · 4:30 PM. Which suits you best?" },
  { from: "guest", text: "11am please" },
  { from: "ai",    text: "✅ Deep Tissue Massage — Saturday 11:00 AM confirmed!" },
];

/* ─── CHAT BUBBLE ───────────────────────────────────────────────── */
function Bubble({
  msg, color, delay,
}: { msg: { from: string; text: string }; color: string; delay: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold: 0.3 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`hp-bubble hp-bubble--${msg.from} ${visible ? "hp-bubble--in" : ""}`}
      style={{ "--delay": `${delay}ms`, "--accent": color } as React.CSSProperties}
    >
      <span>{msg.text}</span>
    </div>
  );
}

/* ─── HERO CARD ─────────────────────────────────────────────────── */
function HeroCard({
  conv, index,
}: { conv: (typeof heroConversations)[0]; index: number }) {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 200 + index * 280);
    return () => clearTimeout(t);
  }, [index]);

  return (
    <div
      ref={ref}
      className={`hp-hero-card ${visible ? "hp-hero-card--in" : ""}`}
      style={{ "--accent": conv.color, "--accent-light": conv.lightColor } as React.CSSProperties}
    >
      <div className="hp-hero-card__agent">
        <span className="hp-hero-card__dot" />
        {conv.agent}
      </div>
      {conv.messages.map((m, i) => (
        <div
          key={i}
          className={`hp-hero-card__msg hp-hero-card__msg--${m.from}`}
          style={{ animationDelay: `${0.5 + index * 0.3 + i * 0.18}s` } as React.CSSProperties}
        >
          {m.text}
        </div>
      ))}
    </div>
  );
}

/* ─── AGENT SECTION ─────────────────────────────────────────────── */
function AgentSection({
  name, role, tagline, desc, bullets, chat, color, lightColor, dark = false,
}: {
  name: string; role: string; tagline: string; desc: string;
  bullets: string[]; chat: { from: string; text: string }[];
  color: string; lightColor: string; dark?: boolean;
}) {
  return (
    <section
      className={`hp-agent ${dark ? "hp-agent--dark" : "hp-agent--light"}`}
      style={{ "--accent": color, "--accent-light": lightColor } as React.CSSProperties}
    >
      <div className="hp-agent__inner">
        <div className="hp-agent__copy">
          <p className="hp-agent__tag">{name}</p>
          <h2 className="hp-agent__heading">{tagline}</h2>
          <p className="hp-agent__role">{role}</p>
          <p className="hp-agent__desc">{desc}</p>
          <ul className="hp-agent__bullets">
            {bullets.map((b, i) => (
              <li key={i}>
                <span className="hp-agent__bullet-dot" />
                {b}
              </li>
            ))}
          </ul>
        </div>

        <div className="hp-agent__chat">
          <div className="hp-agent__chat-header">
            <span className="hp-agent__chat-dot hp-agent__chat-dot--live" />
            <span className="hp-agent__chat-name">{name}</span>
            <span className="hp-agent__chat-status">Live</span>
          </div>
          <div className="hp-agent__chat-body">
            {chat.map((m, i) => (
              <Bubble key={i} msg={m} color={color} delay={i * 120} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── MAIN PAGE ─────────────────────────────────────────────────── */
export default function HomePage() {
  const [loaded, setLoaded] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setLoaded(true), 60);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className={`hp ${loaded ? "hp--loaded" : ""}`}>

      {/* ── NAV ───────────────────────────────────────────── */}
      <nav className="hp-nav">
        <a href="/" className="hp-nav__logo">
          <img src={Logo} alt="AI²" />
        </a>
        <div className={`hp-nav__links ${menuOpen ? "hp-nav__links--open" : ""}`}>
          <a href="/about">About</a>
          <a href="/agents">Agents</a>
          <a href="/bots">Bots</a>
          <a href="/hotel">Demo</a>
        </div>
        <div className="hp-nav__right">
          <a href="/dashboard/login" className="hp-nav__cta">
            Open dashboard <ArrowUpRight size={14} />
          </a>
          <button
            className="hp-nav__burger"
            onClick={() => setMenuOpen(v => !v)}
            aria-label="menu"
          >
            <span /><span /><span />
          </button>
        </div>
      </nav>

      {/* ── HERO ──────────────────────────────────────────── */}
      <section className="hp-hero">
        <div className="hp-hero__top">
          <p className="hp-hero__eyebrow">AI agents for hospitality</p>
          <h1 className="hp-hero__headline">
            Your hotel<br />
            <em>runs itself.</em>
          </h1>
          <p className="hp-hero__sub">
            EVA, DINE, and SERENE handle every guest conversation —
            <br />from booking to checkout, across WhatsApp and Instagram.
          </p>
          <div className="hp-hero__actions">
            <a href="/hotel" className="hp-btn hp-btn--primary">
              See it live <ArrowRight size={16} />
            </a>
            <a href="/about" className="hp-btn hp-btn--ghost">
              Learn more
            </a>
          </div>
        </div>

        <div className="hp-hero__cards">
          {heroConversations.map((c, i) => (
            <HeroCard key={c.agent} conv={c} index={i} />
          ))}
        </div>
      </section>

      {/* ── TICKER ────────────────────────────────────────── */}
      <div className="hp-ticker">
        <div className="hp-ticker__track">
          {["EVA · Hotels", "DINE · Restaurants", "SERENE · Spas", "EVA · Hotels", "DINE · Restaurants", "SERENE · Spas", "EVA · Hotels", "DINE · Restaurants", "SERENE · Spas"].map((t, i) => (
            <span key={i}>{t} <span className="hp-ticker__sep">—</span></span>
          ))}
        </div>
      </div>

      {/* ── EDITORIAL STAT BREAK ──────────────────────────── */}
      <section className="hp-stats">
        <div className="hp-stat">
          <span className="hp-stat__num">3.2M</span>
          <p className="hp-stat__prose">
            guest messages handled last year,<br />
            without a single missed reply.
          </p>
        </div>
        <div className="hp-stat">
          <span className="hp-stat__num">94%</span>
          <p className="hp-stat__prose">
            of bookings close in the same<br />
            WhatsApp thread they started in.
          </p>
        </div>
        <div className="hp-stat">
          <span className="hp-stat__num">18s</span>
          <p className="hp-stat__prose">
            average response time,<br />
            any hour, any language.
          </p>
        </div>
      </section>

      {/* ── EVA ───────────────────────────────────────────── */}
      <AgentSection
        name="EVA"
        role="Hotel Concierge Agent"
        tagline="Check-in, upgrades, room service. Handled."
        desc="EVA lives inside every guest conversation — from the moment they book to the moment they leave a review. Connected to your PMS and dashboard in real time."
        bullets={[
          "WhatsApp & Instagram booking flows",
          "Automatic check-in links and room key delivery",
          "Post-stay review requests and loyalty follow-ups",
        ]}
        chat={evaChat}
        color="#C8813A"
        lightColor="#F5E6D3"
        dark={true}
      />

      {/* ── DINE ──────────────────────────────────────────── */}
      <AgentSection
        name="DINE"
        role="Dining & Reservations Agent"
        tagline="Every table filled. Every order upsold."
        desc="DINE handles reservations, menu inquiries, and real-time upselling across your restaurant — with zero hold time and perfect memory of each guest's preferences."
        bullets={[
          "Instant table reservations via WhatsApp",
          "Smart upsell prompts on wine pairings and specials",
          "Wait-time tracking and service quality alerts",
        ]}
        chat={dineChat}
        color="#3A7A57"
        lightColor="#D4EDE0"
        dark={false}
      />

      {/* ── SERENE ────────────────────────────────────────── */}
      <AgentSection
        name="SERENE"
        role="Wellness & Appointment Agent"
        tagline="Appointments booked. Wellness remembered."
        desc="SERENE manages your spa and clinic with warmth and precision — scheduling appointments, sending reminders, and rewarding loyal guests automatically."
        bullets={[
          "Automated appointment booking and reminders",
          "Personalized post-session wellness follow-ups",
          "Loyalty tracking and member reward automation",
        ]}
        chat={sereneChat}
        color="#6B4E92"
        lightColor="#E8DFF2"
        dark={true}
      />

      {/* ── HOW IT WORKS ──────────────────────────────────── */}
      <section className="hp-how">
        <h2 className="hp-how__heading">How it works</h2>
        <div className="hp-how__steps">
          {[
            { n: "01", title: "Connect your property", body: "We plug into your existing PMS, calendar, and WhatsApp Business account. No migration, no downtime." },
            { n: "02", title: "Agents go live", body: "EVA, DINE, and SERENE begin handling conversations immediately — trained on your property's specifics." },
            { n: "03", title: "Your team stays in control", body: "Every agent interaction appears in your dashboard. Staff can intervene, override, or escalate at any moment." },
          ].map((s) => (
            <div key={s.n} className="hp-how__step">
              <span className="hp-how__step-num">{s.n}</span>
              <h3>{s.title}</h3>
              <p>{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── CLOSING CTA ───────────────────────────────────── */}
      <section className="hp-close">
        <div className="hp-close__inner">
          <p className="hp-close__label">Ready to run itself?</p>
          <h2 className="hp-close__heading">
            The best hospitality<br />
            <em>is invisible.</em>
          </h2>
          <p className="hp-close__sub">
            Let AI² handle the conversations so your team can focus on the moments that actually matter.
          </p>
          <a href="/hotel" className="hp-btn hp-btn--primary hp-btn--lg">
            Book a live demo <ArrowRight size={18} />
          </a>
        </div>
      </section>

      {/* ── FOOTER ────────────────────────────────────────── */}
      <footer className="hp-footer">
        <div className="hp-footer__inner">
          <div className="hp-footer__brand">
            <img src={Logo} alt="AI²" className="hp-footer__logo" />
            <p>AI agents for hotels, restaurants, and wellness spaces.</p>
          </div>
          <div className="hp-footer__links">
            <a href="/about">About</a>
            <a href="/agents">Agents</a>
            <a href="/bots">Bots</a>
            <a href="/hotel">Demo</a>
            <a href="/dashboard/login">Dashboard</a>
          </div>
        </div>
        <div className="hp-footer__bottom">
          <span>© 2025 AI Squared. All rights reserved.</span>
        </div>
      </footer>
    </div>
  );
}
